<template>
  <div
    :data-testid="`overlay-row-${overlay.name}`"
    class="row"
    :class="`${dragHover ?? ''}`"
    :style="{backgroundColor: hovered ? '#eeeeee' : ''}"
    :draggable="isDraggable && canCurrentUserEdit"
    @click="isVisible = !isVisible"
    @dragstart="$emit('dragHandler', $event)"
    @dragend="handleDragEnd"
    @drop="dropHandler($event)"
    @dragenter.stop.prevent="toggleDragClass"
    @dragover.stop.prevent="toggleDragClass"
    @dragleave.stop.prevent="dragHover = null"
    @mouseenter="hovered = true"
    @mouseleave="hovered = false"
  >
    <DragIndicator v-if="isDraggable && canCurrentUserEdit" />
    <v-checkbox-btn
      :model-value="isVisible"
      color="primary"
      density="comfortable"
      :ripple="false"
      :data-testid="`overlay-checkbox-${overlay.name}`"
      style="max-width: 36px;"
      @click.stop
      @update:model-value="handleCheckboxChange"
    />
    <span
      class="overlay-name"
      @click.stop="handleCheckboxChange(!isVisible)"
    >{{ overlay.name }}</span>
    <div
      v-if="hovered && canCurrentUserEdit"
      class="pencil-container"
      :data-testid="`overlay-action-${overlay.name}`"
      @click.stop="handleSelectOverlay(overlay)"
    >
      <PencilIcon :data-testid="`overlay-edit-${overlay.name}`" />
    </div>
    <div
      v-else-if="hovered"
      :data-testid="`overlay-action-${overlay.name}`"
      style="width: 25px; display: flex; justify-content: center; margin: 4px;"
    >
      <v-icon
        size="20px"
        variant="text"
        icon="mdi-bullseye-arrow"
        :data-testid="`overlay-view-${overlay.name}`"
        @click.stop="handleSelectOverlay(overlay)"
      />
    </div>
  </div>
</template>

<script>
// Components
import DragIndicator from '@/components/svgs/DragIndicator.vue';
import PencilIcon from '@/components/svgs/ToolbarIcons/PencilIcon.vue';
import {currentUser} from '@/services/FirestoreService';
import {mapActions} from 'vuex';
import {mapState} from 'vuex';

// Tools & Services
import {mapMutations} from 'vuex';

export default {
  name: 'OverlayRow',
  components: {
    DragIndicator,
    PencilIcon,
  },
  props: {
    overlay: {
      type: Object,
      required: true,
    },
    isDraggable: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['dragHandler', 'dropHandler'],
  data() {
    return {
      hovered: false,
      dragHover: null,
    };
  },
  computed: {
    ...mapState(['taxTeamIds']),
    isVisible: {
      get() {
        return this.overlay.visible;
      },
      set(event) {
        this.overlay.setVisibility(event);
      },
    },
    canCurrentUserEdit() {
      // any user can edit non-tax overlays
      if (!this.overlay.isTaxOverlay) return true;
      // only tax team members can edit tax overlays
      return this.taxTeamIds.includes(currentUser().uid);
    },
  },
  methods: {
    ...mapMutations(['setSelectedOverlay']),
    ...mapActions('infowindow', ['closeInfoWindow']),
    ...mapActions('filters', ['setSectionChecked']),
    /**
     * Handles overlay checkbox changes: updates visibility, ensures Project Overlays section is checked
     * @param {boolean} checked
     */
    async handleCheckboxChange(checked) {
      this.overlay.setVisibility(checked);
      // Ensure "Project Overlays" section is visible/checked
      await this.setSectionChecked({
        market: 'Industrial',
        section: 'Project Overlays',
        checked: true,
      });
    },
    async handleSelectOverlay(overlay) {
      overlay.selectedFromMenu = false;
      this.setSelectedOverlay(overlay);
      // Ensure "Project Overlays" section is visible/checked
      await this.setSectionChecked({
        market: 'Industrial',
        section: 'Project Overlays',
        checked: true,
      });
      // close the info window when the overlay is selected
      await this.closeInfoWindow();
    },
    handleDragEnd() {
      this.hovered = false;
      this.dragHover = null;
    },
    dropHandler(e) {
      this.hovered = false;
      this.dragHover = null;
      this.$emit('dropHandler', e, e.offsetY >= (e.target.offsetHeight / 2) ? 'after' : 'before');
    },
    toggleDragClass(e) {
      if (e?.offsetY >= 0) {
        this.dragHover = e.offsetY >= (e.target.offsetHeight / 2) ? 'drop-bottom' : 'drop-top';
      } else {
        this.dragHover = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
// Styles for overlay row sections
.row {
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
  height: 40px;
}

// Third section of an overlay row is the name
.overlay-name {
  flex-grow:1;
  color: rgb(61, 70, 68);
  max-width: 315px;
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font: normal normal normal 14px/40px main.$fontPrimary;
}

// Fourth section of an overlay row is the edit pencil
.pencil-container {
  display: flex;
  height: 25px;
  width: 25px;
  margin: 4px;
  justify-content: center;
  align-items: center;
}

// Styles for adding a blue line relative to the drag/drop position
.drop-top {
  border-top: solid 2px main.$linkBlue;
}
.drop-bottom {
  border-bottom: solid 2px main.$linkBlue;
}
</style>
